import * as React from "react";
import { Link as RouterLink, type To } from "react-router-dom";

import { cn } from "@/shadcn/lib/utils";

export type LinkProps = {
  readonly to?: To;
  readonly href?: string;
  /** When true (or when `href` is set), renders an external `<a>`. */
  readonly external?: boolean;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> &
  Omit<React.ComponentPropsWithoutRef<typeof RouterLink>, "to">;

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, to, href, external, children, rel, ...props }, ref) => {
    const isExternal =
      external === true ||
      (typeof href === "string" && href.length > 0) ||
      (typeof to === "string" && /^(https?:)?\/\//.test(to) && external !== false);

    const classes = cn("text-link underline-offset-4 hover:underline", className);

    if (isExternal) {
      const resolvedHref = href ?? (typeof to === "string" ? to : "");
      return (
        <a
          className={classes}
          href={resolvedHref}
          ref={ref}
          rel={rel ?? "noopener noreferrer external"}
          {...props}
        >
          {children}
        </a>
      );
    }

    if (!to) {
      return (
        <a className={classes} ref={ref} rel={rel} {...props}>
          {children}
        </a>
      );
    }

    return (
      <RouterLink className={classes} ref={ref} to={to} {...props}>
        {children}
      </RouterLink>
    );
  },
);

Link.displayName = "Link";
